import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const CONFETTI_COLORS = ['#FF4757', '#FFD700', '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899'];

function Confetti() {
  const [pieces, setPieces] = useState([]);

  useEffect(() => {
    const items = Array.from({ length: 80 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 3,
      duration: 2.5 + Math.random() * 3,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 6 + Math.random() * 10,
      rotation: Math.random() * 360,
      shape: Math.random() > 0.5 ? 'circle' : 'rect',
      drift: (Math.random() - 0.5) * 200,
    }));
    setPieces(items);
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          left: `${p.x}%`,
          top: '-20px',
          width: p.size,
          height: p.shape === 'circle' ? p.size : p.size * 0.6,
          borderRadius: p.shape === 'circle' ? '50%' : '2px',
          background: p.color,
          animation: `confettiFall ${p.duration}s ${p.delay}s ease-in infinite`,
          transform: `rotate(${p.rotation}deg)`,
          opacity: 0,
        }} />
      ))}
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(0) translateX(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) translateX(${0}px) rotate(720deg); opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

function FloatingEmoji({ emoji, style }) {
  return (
    <span style={{
      position: 'absolute',
      fontSize: '2rem',
      animation: 'floatUp 3s ease-in-out infinite',
      ...style,
    }}>{emoji}</span>
  );
}

export default function ConfirmationPage() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(8);
  const [showContent, setShowContent] = useState(false);
  const [bounce, setBounce] = useState(false);

  useEffect(() => {
    // Apparition progressive
    setTimeout(() => setShowContent(true), 100);
    setTimeout(() => setBounce(true), 600);

    // Compte à rebours puis redirect
    const interval = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(interval);
          navigate('/auth');
          return 0;
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #FFF0F1 0%, #FFF8E7 40%, #E8F5FF 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px', position: 'relative', overflow: 'hidden',
      fontFamily: 'var(--font-body)',
    }}>

      <Confetti />

      {/* Emojis flottants décoratifs */}
      <FloatingEmoji emoji="🍕" style={{ top: '10%', left: '5%', animationDelay: '0s' }} />
      <FloatingEmoji emoji="🍣" style={{ top: '20%', right: '8%', animationDelay: '0.5s' }} />
      <FloatingEmoji emoji="🍔" style={{ top: '60%', left: '3%', animationDelay: '1s' }} />
      <FloatingEmoji emoji="🌮" style={{ bottom: '15%', right: '5%', animationDelay: '0.3s' }} />
      <FloatingEmoji emoji="🍜" style={{ top: '75%', left: '10%', animationDelay: '1.5s' }} />
      <FloatingEmoji emoji="🥐" style={{ top: '8%', right: '20%', animationDelay: '0.8s' }} />
      <FloatingEmoji emoji="⭐" style={{ bottom: '25%', left: '15%', animationDelay: '0.2s' }} />
      <FloatingEmoji emoji="🎉" style={{ top: '35%', right: '3%', animationDelay: '1.2s' }} />

      {/* Carte principale */}
      <div style={{
        position: 'relative', zIndex: 10,
        background: 'white',
        borderRadius: '32px',
        padding: 'clamp(32px, 6vw, 56px)',
        maxWidth: '520px',
        width: '100%',
        boxShadow: '0 32px 80px rgba(255,71,87,0.15), 0 8px 32px rgba(0,0,0,0.08)',
        textAlign: 'center',
        opacity: showContent ? 1 : 0,
        transform: showContent ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
        transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>

        {/* Icône principale avec bounce */}
        <div style={{
          fontSize: '80px',
          marginBottom: '8px',
          display: 'block',
          animation: bounce ? 'bigBounce 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
          lineHeight: 1,
        }}>
          ✅
        </div>

        {/* Badge "Officiel" */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: 'linear-gradient(135deg, #10B981, #059669)',
          color: 'white', borderRadius: '50px',
          padding: '4px 14px', fontSize: '12px', fontWeight: 700,
          marginBottom: '20px', letterSpacing: '0.05em',
          textTransform: 'uppercase',
          animation: 'popIn 0.4s 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}>
          🎊 Bienvenue dans la famille !
        </div>

        {/* Titre */}
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'clamp(26px, 5vw, 36px)',
          fontWeight: 800,
          lineHeight: 1.2,
          marginBottom: '12px',
          background: 'linear-gradient(135deg, var(--brand), #FF8C00)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'popIn 0.4s 0.2s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}>
          Adresse email<br />confirmée ! 🥳
        </h1>

        {/* Sous-titre */}
        <p style={{
          color: 'var(--text-2)', fontSize: '16px', lineHeight: 1.6,
          marginBottom: '28px',
          animation: 'popIn 0.4s 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}>
          Votre compte <strong>Foodies</strong> est prêt.<br />
          Vous pouvez maintenant découvrir et partager<br />les meilleurs restaurants avec vos amis. 🍜
        </p>

        {/* Fun facts row */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px',
          marginBottom: '28px',
          animation: 'popIn 0.4s 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}>
          {[
            { emoji: '🍽️', text: 'Ajoutez', sub: 'des restaus' },
            { emoji: '⭐', text: 'Notez', sub: 'vos visites' },
            { emoji: '👫', text: 'Partagez', sub: 'avec vos amis' },
          ].map((item, i) => (
            <div key={i} style={{
              background: 'var(--surface-2)', borderRadius: '14px',
              padding: '12px 8px',
              transition: 'transform 0.2s',
            }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>{item.emoji}</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>{item.text}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{item.sub}</div>
            </div>
          ))}
        </div>

        {/* Bouton + compteur */}
        <div style={{ animation: 'popIn 0.4s 0.65s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}>
          <button onClick={() => navigate('/auth')} style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'linear-gradient(135deg, var(--brand), #FF8C00)',
            color: 'white', border: 'none', borderRadius: '14px',
            padding: '14px 32px', fontSize: '16px', fontWeight: 700,
            cursor: 'pointer', width: '100%', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(255,71,87,0.35)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            marginBottom: '12px',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(255,71,87,0.45)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255,71,87,0.35)'; }}
          >
            🚀 Commencer l'aventure
          </button>

          <p style={{ fontSize: '13px', color: 'var(--text-3)' }}>
            Redirection automatique dans{' '}
            <span style={{ fontWeight: 700, color: 'var(--brand)', fontSize: '15px' }}>{countdown}</span>
            {' '}secondes…
          </p>
        </div>

        {/* Barre de progression du countdown */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: '4px', background: 'var(--surface-3)', borderRadius: '0 0 32px 32px', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, var(--brand), #FF8C00)',
            borderRadius: '0 0 32px 32px',
            width: `${(countdown / 8) * 100}%`,
            transition: 'width 1s linear',
          }} />
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Inter:wght@400;500;600;700&display=swap');

        :root {
          --brand: #FF4757;
          --text: #0F0F1A;
          --text-2: #4A4A6A;
          --text-3: #9090B0;
          --surface-2: #F8F8FA;
          --surface-3: #F0F0F5;
          --font-display: 'Syne', sans-serif;
          --font-body: 'Inter', sans-serif;
        }

        @keyframes bigBounce {
          0%   { transform: scale(0.3) rotate(-15deg); opacity: 0; }
          50%  { transform: scale(1.3) rotate(5deg); opacity: 1; }
          70%  { transform: scale(0.9) rotate(-2deg); }
          100% { transform: scale(1) rotate(0deg); }
        }

        @keyframes popIn {
          from { opacity: 0; transform: translateY(16px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes floatUp {
          0%   { transform: translateY(0px) rotate(0deg); opacity: 0.7; }
          50%  { transform: translateY(-20px) rotate(10deg); opacity: 1; }
          100% { transform: translateY(0px) rotate(0deg); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
